import * as React from "react";
import { useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { SearchIcon } from "affinityIcons";
import { isNil } from "ramda";

import { NavigateButton } from "affinityUI/elements/button";
import { Grid } from "affinityUI/elements/grid";
import { EmptyState } from "affinityUI/features/emptyState";
import { Heading } from "affinityUI/structures/heading";
import { Layout } from "affinityUI/structures/layout";
import { Restricted, useAuth } from "lib/auth";
import { PERMISSIONS } from "lib/biot/roles/types";

import { PatientSearch } from "../../../app/components/patientSearch";
import { PatientCard } from "../../../views/patientDetails/patients/patientCard";
import { useSearchPatients } from "../patients/hooks/useSearchPatients";

export const SearchPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const searchTerm = useMemo(() => searchParams.get("term"), [searchParams]);

  const { patients, loading, search } = useSearchPatients();

  useEffect(() => {
    if (searchTerm) {
      search(searchTerm);
    }
  }, [searchTerm, search]);

  const [{ isPermitted }] = useAuth();
  const isPermitAddPatient = isPermitted([PERMISSIONS.PATIENT_ADD]);

  const actions = (
    <Restricted permits={[PERMISSIONS.PATIENT_ADD]}>
      <Link to="add">Add patient</Link>
    </Restricted>
  );

  return (
    <Layout
      loading={loading}
      title="Search results"
      backLink="/patients"
      showBackBtn
      backLinkText="Back to patients page"
      actions={<PatientSearch />}
    >
      <Grid>
        {!isNil(patients) && Boolean(patients?.length) && (
          <>
            <Heading>
              <Heading.Meta data-testid="search-meta">
                <strong>
                  {patients?.length} {patients?.length === 1 ? "result" : "results"} based on &quot;
                  {searchParams.get("term")}&quot; have been found
                </strong>
              </Heading.Meta>
              <Heading.Actions>{actions}</Heading.Actions>
            </Heading>
            {patients.map((patient) => (
              <PatientCard
                data-testid="search-list-item"
                key={patient.id}
                searchTerm={searchParams.get("term")}
                patient={patient}
              />
            ))}
          </>
        )}
        {!patients?.length && (
          <EmptyState>
            <EmptyState.Icon>
              <SearchIcon />
            </EmptyState.Icon>
            <EmptyState.Message>
              <h3>No patients found</h3>
              Your search <strong>&quot;{searchParams.get("term")}&quot;</strong> does not match any existing
              information. You can search by Patient name, NHS / Hospital number or Patient I.D.
              {isPermitAddPatient && " Alternatively, you can add a new patient."}
            </EmptyState.Message>
            {isPermitAddPatient && (
              <EmptyState.Message>
                <NavigateButton to="../add">Create patient</NavigateButton>
              </EmptyState.Message>
            )}
          </EmptyState>
        )}
      </Grid>
    </Layout>
  );
};
